export type ThemeMode = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'portfolio-theme';

type ThemeListener = (mode: ThemeMode) => void;

const listeners = new Set<ThemeListener>();

function isBrowser(): boolean {
  return typeof window !== 'undefined';
}

export function getSystemTheme(): ResolvedTheme {
  if (!isBrowser()) return 'dark';
  return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
}

export function getStoredTheme(): ThemeMode {
  if (!isBrowser()) return 'system';
  const stored = localStorage.getItem(THEME_STORAGE_KEY);
  if (stored === 'light' || stored === 'dark' || stored === 'system') {
    return stored;
  }
  return 'system';
}

export function resolveTheme(mode: ThemeMode): ResolvedTheme {
  return mode === 'system' ? getSystemTheme() : mode;
}

export function applyTheme(mode: ThemeMode): ResolvedTheme {
  const resolved = resolveTheme(mode);
  if (isBrowser()) {
    document.documentElement.classList.toggle('dark', resolved === 'dark');
    document.documentElement.style.colorScheme = resolved;
  }
  return resolved;
}

export function setThemeMode(mode: ThemeMode): void {
  if (!isBrowser()) return;
  localStorage.setItem(THEME_STORAGE_KEY, mode);
  applyTheme(mode);
  listeners.forEach((listener) => listener(mode));
}

export function subscribeTheme(listener: ThemeListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function watchSystemTheme(callback: (theme: ResolvedTheme) => void): () => void {
  if (!isBrowser() || !window.matchMedia) {
    return () => undefined;
  }
  const mq = window.matchMedia('(prefers-color-scheme: dark)');
  if (!mq.addEventListener) {
    return () => undefined;
  }
  const handler = (event: MediaQueryListEvent) => callback(event.matches ? 'dark' : 'light');
  mq.addEventListener('change', handler);
  return () => mq.removeEventListener('change', handler);
}
